import React from 'react';

import { useForm, useFormState } from 'react-final-form';

import { VerificationRequestType } from 'src/api';
import { VerificationRequestCreateFormFields } from 'src/constants/verificationRequest';
import { useVerificationRequestFormData } from 'src/hooks/verificationRequest';
import type { SelectFieldOption } from 'src/types/components';

import { VerificationRequestCreateFormVerificationReportTypeBasicField } from './VerificationRequestCreateFormVerificationReportTypeBasicField';

type VerificationRequestCreateFormVerificationReportTypeFieldProps = {
  options: SelectFieldOption[];
  multiple?: boolean;
  disabled?: boolean;
  disableWhenIncomeUnavailable?: boolean;
};

export const VerificationRequestCreateFormVerificationReportTypeField: React.FC<
  VerificationRequestCreateFormVerificationReportTypeFieldProps
> = ({ options, multiple, disabled, disableWhenIncomeUnavailable = true }) => {
  const { change } = useForm();
  const { values } = useFormState();

  const { isEmployeeExists, isEmployersInfoLoading, isEmployersHavePayrollsInfo } =
    useVerificationRequestFormData();

  const isAnyEmployerSelected = React.useMemo(() => {
    return values.employers && values.employers.length > 0;
  }, [values.employers]);

  const availableOptions = React.useMemo(() => {
    if (!isAnyEmployerSelected || isEmployersHavePayrollsInfo) return options;

    return options.filter(option => option.value !== VerificationRequestType.VerificationOfIncome);
  }, [isAnyEmployerSelected, isEmployersHavePayrollsInfo, options]);

  const selectDefaultValueEventEffect = React.useEffectEvent(
    (availableOptions: SelectFieldOption[]) => {
      if (isAnyEmployerSelected && !isEmployersHavePayrollsInfo) {
        const currentValue = values.verificationReportType;
        const fallbackValue = availableOptions[0]?.value;

        if (multiple && Array.isArray(currentValue)) {
          const nextValue = currentValue.filter(
            type => type !== VerificationRequestType.VerificationOfIncome,
          );
          change(
            VerificationRequestCreateFormFields.verificationReportType,
            nextValue.length ? nextValue : fallbackValue ? [fallbackValue] : [],
          );
          return;
        }

        if (currentValue === VerificationRequestType.VerificationOfIncome) {
          change(VerificationRequestCreateFormFields.verificationReportType, fallbackValue);
        }
      }
    },
  );
  React.useEffect(() => {
    selectDefaultValueEventEffect(availableOptions);
  }, [availableOptions]);

  return (
    <VerificationRequestCreateFormVerificationReportTypeBasicField
      options={availableOptions}
      multiple={multiple}
      disabled={
        !isEmployeeExists ||
        isEmployersInfoLoading ||
        !isAnyEmployerSelected ||
        (disableWhenIncomeUnavailable && !isEmployersHavePayrollsInfo) ||
        disabled
      }
      showIncomeDataWarning={isAnyEmployerSelected && !isEmployersHavePayrollsInfo}
    />
  );
};
